import { type PropsWithChildren, Suspense, useState } from 'react';
import { Trans, useLingui } from '@lingui/react';
import { Box } from '@startuptools/ui/Box';
import { Badge } from '@startuptools/ui/Badge';
import { Divider } from '@startuptools/ui/Divider';
import { List } from '@startuptools/ui/List';
import { ListItem } from '@startuptools/ui/ListItem';
import { ListItemButton, listItemButtonClasses } from '@startuptools/ui/ListItemButton';
import { ListItemContent } from '@startuptools/ui/ListItemContent';
import { Typography } from '@startuptools/ui/Typography';
import { Sheet } from '@startuptools/ui/Sheet';
import GlobalStyles from '@mui/joy/GlobalStyles';
import { ErrorBoundary } from '@startuptools/ui/ErrorBoundary';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import { Logo } from '@startuptools/ui/Logo';
import { Button } from '@startuptools/ui/Button';
import { useTheme } from '@startuptools/ui/Theme';
import DashboardIcon from '@mui/icons-material/Dashboard';
import DescriptionIcon from '@mui/icons-material/Description';
import GroupsIcon from '@mui/icons-material/Groups';
import InventoryIcon from '@mui/icons-material/Inventory';
import AssessmentIcon from '@mui/icons-material/Assessment';
import HourglassTopIcon from '@mui/icons-material/HourglassTop';
import InsightsIcon from '@mui/icons-material/Insights';
import SettingsIcon from '@mui/icons-material/Settings';
import { Stack } from '@startuptools/ui/Stack';
import { type IconComponent } from '@startuptools/ui';
import { closeSidebar } from './utils';
import { switchLang } from '../../helpers/switch-lang';
import { SwitchLangMenuItem } from '@startuptools/business-ui/profile-menu/menu-items/SwitchLangMenuItem';
import { useCompanyId } from '@startuptools/common/react-wrapper';
import { useSideNavQuery, useSideNavSuspenseQuery } from './side-nav.graphql';
import { CompanySelect } from './CompanySelect';
import { StatusNotification } from '../status/StatusNotification';
import { CompanySubscriptionCard } from './CompanySubscription';
import { Alert } from '@startuptools/ui/Alert';
import { ProfileMenu } from '@startuptools/business-ui/profile-menu/ProfileMenu';
import { ListItemDecorator } from '@startuptools/ui/joy/ListItemDecorator';
import { MenuItem } from '@startuptools/ui/MenuItem';
import { Gravatar } from '@startuptools/ui/Gravatar';
import { Chip } from '@startuptools/ui/Chip';
import ShieldIcon from '@mui/icons-material/Shield';
import { logout, stopImitatingUser } from '@startuptools/auth/ng';
import { TermsMenuItem } from '@startuptools/business-ui/profile-menu/menu-items/TermsMenuItem';
import { LogoutMenuItem } from '@startuptools/business-ui/profile-menu/menu-items/LogoutMenuItem';
import { UserProfileDialog } from '../../user/settings/UserProfileDialog';
import { captureException } from '@sentry/react';
import { toast } from '@startuptools/ui/toast/use-toast';
import { useCompanyDeleteMutation } from '../../graphql/react-operations';
import { AlertConfirmDialog } from '../simple-dialogs/AlertConfirmDialog';
import { Link, useMatch } from 'react-router-dom';
import { useNavigate } from '../Navigate';
import { useUserId } from '../react-wrapper/UserIdContext';

const Toggler = ({
  defaultExpanded = false,
  renderToggle,
  children,
  render,
}: {
  defaultExpanded?: boolean;
  children: React.ReactNode;
  renderToggle: (params: { open: boolean; setOpen: React.Dispatch<React.SetStateAction<boolean>> }) => React.ReactNode;
  render: boolean;
}) => {
  const [open, setOpen] = useState(defaultExpanded);
  if (!render) return renderToggle({ open, setOpen });
  return (
    <>
      {renderToggle({ open, setOpen })}
      <Box
        sx={{
          display: 'grid',
          gridTemplateRows: open ? '1fr' : '0fr',
          transition: '0.2s ease',
          '& > *': {
            overflow: 'hidden',
          },
        }}
      >
        {children}
      </Box>
    </>
  );
};

const NavItem = ({
  href,
  label,
  icon: Icon,
  children,
  topLevel,
}: PropsWithChildren<{
  href: string;
  label: React.ReactNode;
  icon?: IconComponent;
  position?: 'top' | 'bottom';
  topLevel?: boolean;
}>) => {
  const { link, pathname } = useNavigate();
  const linkTo = link(href);
  const match = useMatch(`${linkTo}/*`);
  const isActive = Boolean(match);

  return (
    <ListItem nested={Boolean(children)}>
      <Toggler
        key={pathname} /* reset state on navigation */
        defaultExpanded={isActive}
        render={Boolean(children)}
        renderToggle={({ open, setOpen }) => (
          <ListItemButton
            style={{ gap: '6px' }}
            component={children ? 'button' : Link}
            to={children ? undefined : linkTo}
            onClick={() => {
              if (children) {
                return setOpen(!open);
              }
              closeSidebar();
            }}
            role="menuitem"
            selected={Boolean(!children && href && isActive)}
          >
            {Icon && (
              <Icon
                fontSize="small"
                sx={{
                  /* *this should be inherited automatically, dont get why it doesn't. hard coding this
                  for now to move on.*/
                  color: 'var(--Icon-color, var(--joy-palette-text-icon, var(--joy-palette-neutral-500, #636B74)))',
                  fill: 'var(--Icon-color, var(--joy-palette-text-icon, var(--joy-palette-neutral-500, #636B74)))',
                }}
              />
            )}

            <ListItemContent>
              <Typography level={children || topLevel ? 'title-sm' : 'body-sm'}>{label}</Typography>
            </ListItemContent>
            {Boolean(children) && <KeyboardArrowDownIcon sx={{ transform: open ? 'rotate(180deg)' : 'none' }} />}
          </ListItemButton>
        )}
      >
        {children && <List sx={{ gap: 0.5 }}>{children}</List>}
      </Toggler>
    </ListItem>
  );
};

const getCookie = (name: string): string | undefined => {
  const value = `; ${document.cookie}`;
  const parts = value.split(`; ${name}=`);
  if (parts.length === 2) return parts.pop()?.split(';').shift();
  return undefined;
};

const SuspendingArea = ({ userId, imitateUser }: { userId: string; imitateUser: boolean }) => {
  const companyId = useCompanyId();
  const { i18n } = useLingui();
  const [isProfileDialogOpen, setIsProfileDialogOpen] = useState(false);

  const {
    data: { user },
  } = useSideNavSuspenseQuery({ variables: { userId, companyId } });
  return (
    <>
      <ProfileMenu
        identityDisplayProps={{
          name: user.name,
          secondRow: user.email,
          layout: 'column',
          startDecorator: (
            <Gravatar md5Hash={user.emailMd5} uuid={user.id}>
              {user.name?.[0] ?? user.email?.[0]}
            </Gravatar>
          ),
        }}
      >
        <SwitchLangMenuItem switchLang={switchLang} lang={i18n.locale as 'en' | 'sv'} />

        <MenuItem onClick={() => setIsProfileDialogOpen(true)}>
          <ListItemDecorator>
            <SettingsIcon />
          </ListItemDecorator>
          <Trans id="Settings" />
        </MenuItem>

        <TermsMenuItem />

        <Divider />
        {user.admin && (
          <>
            <MenuItem component="a" slotProps={{ root: { href: `/sv/admin`, target: '_blank' } }}>
              <ListItemDecorator>
                <ShieldIcon />
              </ListItemDecorator>
              <Trans id="Back Office" />
            </MenuItem>
            <Divider />
          </>
        )}
        <LogoutMenuItem
          onClick={async () => {
            if (imitateUser) {
              await stopImitatingUser();
            } else {
              await logout();
            }
          }}
        />
      </ProfileMenu>
      {isProfileDialogOpen && <UserProfileDialog onClose={() => setIsProfileDialogOpen(false)} />}
    </>
  );
};

const BottomSection = ({ imitateUser, userId }: { userId: string; imitateUser: boolean }) => {
  return (
    <Stack gap={1}>
      <CompanySubscriptionCard />
      <Divider />
      <ErrorBoundary fallback={<></>}>
        <Stack direction="row" gap={1} alignItems="center">
          <Suspense fallback={<Box width={170} />}>
            <SuspendingArea userId={userId} imitateUser={imitateUser} />
          </Suspense>
        </Stack>
      </ErrorBoundary>
    </Stack>
  );
};

const SideNavItems = ({
  shareRegisterOutOfSync,
  isAntlerCompany,
}: {
  shareRegisterOutOfSync: boolean;
  isAntlerCompany: boolean;
}) => {
  const userId = useUserId();

  if (!userId) {
    throw new Error('no user id found');
  }

  return (
    <>
      <NavItem href="/about" icon={DashboardIcon} label={<Trans id="Overview" />}>
        <NavItem href="/about/dashboard" label={<Trans id="Dashboard" />} />
        <NavItem href="/about/company-directors" label={<Trans id="Board & Accountant" />} />
        <NavItem href="/about/cases" label={<Trans id="Cases" />} />
      </NavItem>
      <NavItem href="/documents" icon={DescriptionIcon} label={<Trans id="Documents & e-signing" />}>
        <NavItem href="/documents/company-documents" label={<Trans id="Documents & e-signing" />} />
        <NavItem href="/documents/free-templates" label={<Trans id="Free Templates" />} />
      </NavItem>
      <NavItem href="/board-portal" icon={GroupsIcon} label={<Trans id="Board Portal" />}>
        <NavItem label={<Trans id="Shareholders' Meetings" />} href="/board-portal/shareholders-meetings" />
        <NavItem label={<Trans id="Board Meetings" />} href="/board-portal/board-meetings" />
        <NavItem label={<Trans id="Authorizations" />} href="/board-portal/authorization-groups" />
        <NavItem label={<Trans id="Articles of Association" />} href="/board-portal/articles-of-assocs" />
      </NavItem>
      <NavItem
        href="/share-register"
        icon={(props: React.ComponentProps<IconComponent>) => (
          <Badge
            invisible={!shareRegisterOutOfSync}
            color="primary"
            anchorOrigin={{
              vertical: 'top',
              horizontal: 'right',
            }}
          >
            <InventoryIcon {...props} />
          </Badge>
        )}
        label={<Trans id="Share Register" />}
      >
        <NavItem label={<Trans id="Overview" />} href="/share-register/share-capital" />
        <NavItem label={<Trans id="Shareholders" />} href="/share-register/overview" />
        <NavItem
          label={
            <Badge
              invisible={!shareRegisterOutOfSync}
              color="primary"
              anchorOrigin={{
                vertical: 'top',
                horizontal: 'right',
              }}
            >
              <Trans id="Transactions" />
            </Badge>
          }
          href="/share-register/transactions"
        />
        <NavItem label={<Trans id="Share Register" />} href="/share-register/full" />
        <NavItem label={<Trans id="Individual Reports" />} href="/share-register/individual" />
        <NavItem label={<Trans id="Transaction Reports" />} href="/share-register/transaction" />
        <NavItem label={<Trans id="Share Certificates" />} href="/share-register/share-certificates" />
      </NavItem>
      <NavItem
        href="/cap-tables"
        icon={AssessmentIcon}
        topLevel
        label={
          <Badge
            badgeContent={<Trans id="Pro" />}
            slotProps={{
              badge: {
                sx: theme => ({
                  right: '-20px',
                  backgroundColor: theme.palette.pro.dark,
                  color: theme.palette.text.primary,
                }),
              },
            }}
          >
            <Trans id="Cap Table" />
          </Badge>
        }
      />
      <NavItem href="/stock-options" icon={HourglassTopIcon} label={<Trans id="Stock options" />}>
        <NavItem label={<Trans id="Overview" />} href="/stock-options/overview" />
        <NavItem label={<Trans id="Warrants" />} href="/stock-options/warrant-programs" />
        <NavItem label={<Trans id="QESOs" />} href="/stock-options/employee-options" />
      </NavItem>
      <NavItem href="/investments" icon={InsightsIcon} label={<Trans id="Investments" />}>
        <NavItem label={<Trans id="Share Issues" />} href="/investments/share-issues" />
        <NavItem label={<Trans id="WISE" />} href="/investments/wise-options" />
        {isAntlerCompany && <NavItem label={<Trans id="Antler-WISE" />} href="/investments/antler-wise-options" />}
      </NavItem>
    </>
  );
};

const Inner = () => {
  const userId = useUserId();
  const companyId = useCompanyId();
  const { navigate, link } = useNavigate();
  const theme = useTheme();
  const imitateUser = getCookie('sut-imitate-user');

  const [showDeleteCompany, setShowDeleteCompany] = useState(false);
  const { data } = useSideNavQuery({ variables: { userId, companyId } });
  // eslint-disable-next-line sut/loading-mutation
  const [doDeleteCompany] = useCompanyDeleteMutation();

  const handleDeleteCompany = async () => {
    try {
      await doDeleteCompany({ variables: { companyId } });
      localStorage.removeItem('last_company_id');
      toast({
        severity: 'success',
        title: <Trans id="The company was permanently removed" />,
      });
      await navigate('/', '');
      setShowDeleteCompany(false);
    } catch (e) {
      captureException(e);
      toast({
        severity: 'danger',
        title: <Trans id="The company could not be removed" />,
      });
    }
  };

  return (
    <Sheet
      className="Sidebar"
      sx={{
        position: { xs: 'fixed', md: 'sticky' },
        transform: {
          xs: 'translateX(calc(100% * (var(--SideNavigation-slideIn, 0) - 1)))',
          md: 'none',
        },
        transition: 'transform 0.4s, width 0.4s',
        zIndex: 1000,
        height: '97.5dvh',
        width: 'var(--Sidebar-width)',
        top: 0,
        p: 2,
        flexShrink: 0,
        display: 'flex',
        flexDirection: 'column',
        gap: 2,
        borderRight: '1px solid',
        borderColor: 'divider',
      }}
    >
      <GlobalStyles
        styles={() => ({
          ':root': {
            '--Sidebar-width': '245px',
          },
        })}
      />
      <Box
        className="Sidebar-overlay"
        sx={{
          position: 'fixed',
          zIndex: 9,
          top: 0,
          left: { xs: 'calc(4 * var(--joy-spacing))', md: 0 },
          width: '100vw',
          height: '100vh',
          opacity: 'var(--SideNavigation-slideIn)',
          backgroundColor: 'var(--joy-palette-background-backdrop)',
          transition: 'opacity 0.4s',
          transform: {
            xs: 'translateX(calc(100% * (var(--SideNavigation-slideIn, 0) - 1) + var(--SideNavigation-slideIn, 0) * var(--Sidebar-width, 0px)))',
            lg: 'translateX(-100%)',
          },
        }}
        onClick={() => closeSidebar()}
      />
      <Stack direction="row" gap={1} justifyContent="center" alignItems="center">
        <Button
          component={Link}
          to={link('/about/dashboard')}
          sx={{
            width: '50%',
            padding: 0,
            background: 'transparent',
            '&:hover': {
              background: 'transparent',
            },
          }}
        >
          <Logo color={theme.palette.primary[500]} />
        </Button>
        <StatusNotification />
      </Stack>

      <Sheet variant="outlined" sx={{ borderRadius: 8, padding: 1 }}>
        <CompanySelect />
      </Sheet>

      <Box
        sx={{
          minHeight: 0,
          overflow: 'hidden auto',
          flexGrow: 1,
          display: 'flex',
          flexDirection: 'column',
          [`& .${listItemButtonClasses.root}`]: {
            gap: 1.5,
          },
        }}
      >
        <List
          size="sm"
          sx={{
            gap: 1,
            '--List-nestedInsetStart': '30px',
            '--ListItem-radius': theme => theme.vars.radius.sm,
          }}
        >
          <SideNavItems
            shareRegisterOutOfSync={!(data?.nextShareCapitalChanges.inSync ?? true)}
            isAntlerCompany={data?.company.isAntlerCompany ?? false}
          />
        </List>

        <List
          size="sm"
          sx={{
            mt: 'auto',
            flexGrow: 0,
            '--List-nestedInsetStart': '30px',
            '--ListItem-radius': theme => theme.vars.radius.sm,
          }}
        >
          <NavItem href="/settings" icon={SettingsIcon} label={<Trans id="Settings" />} position="bottom">
            <NavItem label={<Trans id="Board Meetings" />} href="/settings/board/members" />
            <NavItem label={<Trans id="Users" />} href="/settings/users" />
            <NavItem label={<Trans id="Profile" />} href="/settings/profile" />
            <NavItem label={<Trans id="Billing" />} href="/settings/billing" />
            {data?.user.admin && (
              <>
                <Divider />
                <ListItemButton onClick={() => setShowDeleteCompany(true)}>
                  <Trans
                    id="Delete Company <chip>admin</chip>"
                    components={{ chip: <Chip variant="outlined" color="neutral" size="sm" /> }}
                  />
                </ListItemButton>
              </>
            )}
          </NavItem>
        </List>
      </Box>
      <Stack gap={1} alignItems="center">
        <BottomSection imitateUser={!!imitateUser} userId={imitateUser || userId!} />
      </Stack>
      {showDeleteCompany && (
        <AlertConfirmDialog
          sx={{ width: 600 }}
          onClose={() => setShowDeleteCompany(false)}
          onConfirm={handleDeleteCompany}
          title={<Trans id="Delete Company" />}
          content={
            <Stack gap={2}>
              <Trans
                id="Are you sure that you want to delete the company {companyName}?"
                values={{ companyName: data?.company.name }}
              />
              <Alert color="danger">
                <Trans id="All information included user created and generated data will be permanently removed!" />
              </Alert>
            </Stack>
          }
        />
      )}
    </Sheet>
  );
};

export const SideNav = () => {
  return (
    <Suspense>
      <Inner />
    </Suspense>
  );
};
